import { FunctionComponent } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faCircleCheck, faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

interface FeedUndoToastProps {
	message: string;
	onUndo?: () => void;
	/** Optional secondary action — the strength change's "Add reason" (D1). */
	onAddReason?: () => void;
	/** Disables the actions while the undo request is in flight. */
	isBusy?: boolean;
	/** "error" renders the exclamation icon (failed ignore, D3). */
	variant?: "success" | "error";
}

/**
 * Shared undo toast of the feed (D1 strength change, D3 run ignore) — purely
 * presentational and portaled bottom-center, modeled on {@link UndoSendToast}.
 * Lifetime (auto-hide / commit) is the owner's concern.
 */
export const FeedUndoToast: FunctionComponent<FeedUndoToastProps> = ({
	message,
	onUndo,
	onAddReason,
	isBusy = false,
	variant = "success",
}) => {
	const { t } = useTranslation();

	return ReactDOM.createPortal(
		<div
			className="fixed bottom-6 left-1/2 z-[9999] flex -translate-x-1/2 items-center gap-3 rounded-xl bg-gray-900 px-4 py-2.5 text-sm text-white shadow-lg"
			data-testid="feed-undo-toast"
		>
			<Icon
				className="h-4 w-4 shrink-0"
				icon={variant === "error" ? faCircleExclamation : faCircleCheck}
			/>
			<span>{message}</span>
			{onAddReason && (
				<button
					className="cursor-pointer rounded-lg px-2 py-1 text-sm font-semibold text-white/80 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
					data-testid="feed-undo-toast-add-reason"
					disabled={isBusy}
					type="button"
					onClick={onAddReason}
				>
					{t("feed.runCard.strength.addReason")}
				</button>
			)}
			{onUndo && (
				<button
					className="cursor-pointer rounded-lg bg-white/15 px-3 py-1 text-sm font-bold text-white hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-60"
					data-testid="feed-undo-toast-undo"
					disabled={isBusy}
					type="button"
					onClick={onUndo}
				>
					{t("feed.runCard.strength.undo")}
				</button>
			)}
		</div>,
		document.body
	);
};
